import React, { useEffect, useState } from "react";
import { Paper, IconButton, Chip, Avatar, Tooltip } from "@mui/material";
import MUIDataTable from "../../../components/LegacyMuiDataTable";
import EditIcon from "@mui/icons-material/Edit";
import Delete from "@mui/icons-material/Delete";
import { useSelector, useDispatch } from "react-redux";
import { green } from "@mui/material/colors";
import { useSnackbar } from "notistack";
import CheckIcon from "@mui/icons-material/Check";
import RemoveIcon from "@mui/icons-material/Remove";

import { getLayouts, deleteLayout } from "../../../store/ducks/duckLayout";
import CustomToolbar from "../../../components/CustomToolbar";
import NavBar from "../../../components/Navbar";
import { getMenuItems } from "../../../store/ducks/duckMenuItem";
import { BorderLinearProgress } from "../../../components/LoadingComponent";
import ConfirmDelete from "../../../components/confirmDelete";
import { useStylesLayout } from "../../../styles/Layout";

import LayoutForm from "./components/LayoutForm";

const initialLayout = {
  id: 0,
  name: "",
  description: "",
  deleted: false,
  isEconomics: false,
};

const Layout = () => {
  const data = useSelector((state) => state.layout.dataLayout);
  const loading = useSelector((state) => state.layout.isLoading);
  const menuItems = useSelector((state) => state.menuItem.data);
  const paths = useSelector((state) => state.sidebar.paths);
  const { enqueueSnackbar } = useSnackbar();

  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [id, setId] = useState(0);
  const [name, setName] = useState("");
  const [isEdit, setEdit] = useState(false);

  //const [openPreview, setOpenPreview]= useState(false)
  const [layout, setLayout] = useState(initialLayout);

  const [openConfirm, setOpenConfirm] = useState(false);

  const classes = useStylesLayout();

  const getLayoutName = (id) => data.find((x) => x.id === id)?.name ?? "";
  const getMenuItemName = (id) => menuItems.find((x) => x.id === id)?.name ?? "";

  useEffect(() => {
    dispatch(getLayouts());
    dispatch(getMenuItems());
  }, [dispatch]);

  const handleClickOpen =
    (layout, isEdit = true) =>
    (event) => {
      event.preventDefault();

      setId(layout?.id || 0);
      setName(layout?.name || "");
      setLayout(layout);
      setEdit(isEdit);

      setOpen(true);
    };

  const handleClickReload = () => {
    dispatch(getLayouts());
    dispatch(getMenuItems());
  };

  const handleClose = () => {
    setLayout(initialLayout);
    setOpen(false);
  };
  const handleCloseConfirm = () => {
    setOpenConfirm(false);
  };

  const handleConfirm = (layout) => (event) => {
    event.preventDefault();
    setId(layout?.id || 0);
    setName(layout?.name || "");

    setOpenConfirm(true);
  };

  const handleDelete = (event) => {
    event.preventDefault();
    dispatch(deleteLayout(id)).then(() =>
      enqueueSnackbar(`Layout ${name} Deleted succesfully`, { variant: "info" }),
    );
    handleCloseConfirm();
  };

  const columns = [
    {
      name: "id",
      label: "Id",
      options: {
        filter: true,
        display: false,
        customFilterListOptions: {
          render: (v) => getLayoutName(v),
        },
        filterOptions: {
          renderValue: (v) => getLayoutName(v),
        },
      },
    },
    {
      name: "deleted",
      label: "Deleted",
      options: {
        filter: true,
        display: false,
        filterType: "dropdown",
        customFilterListOptions: { render: (v) => `Deleted: ${v}` },
        filterList: [false],
      },
    },
    {
      name: "name",
      label: "Name",
      options: {
        filter: true,
        setCellHeaderProps: () => ({
          style: {
            whiteSpace: "nowrap",
            position: "sticky",
            left: 0,
            background: "white",
            zIndex: 101,
          },
        }),
      },
    },
    {
      name: "description",
      label: "Description",
      options: {
        filter: true,
        setCellHeaderProps: () => ({
          style: {
            whiteSpace: "nowrap",
            position: "sticky",
            left: 0,
            background: "white",
            zIndex: 101,
          },
        }),
      },
    },
    {
      name: "isEconomics",
      label: "Economics Rule",
      options: {
        filter: true,
        customBodyRenderLite: (dataIndex) => {
          let rowData = data[dataIndex];

          return rowData.isEconomics ? <CheckIcon /> : <RemoveIcon />;
        },
      },
    },
    {
      name: "testing",
      label: "Testing",
      options: {
        filter: true,
        customBodyRenderLite: (dataIndex) => {
          let testing = data[dataIndex]?.testing;

          return testing ? <CheckIcon /> : null;
        },
        setCellHeaderProps: () => ({
          style: {
            whiteSpace: "nowrap",
            position: "sticky",
            left: 0,
            background: "white",
            zIndex: 101,
          },
        }),
      },
    },
    {
      name: "menuItemId",
      label: "Menu Item",
      options: {
        filter: true,
        customBodyRenderLite: (dataIndex) => {
          let { menuItemId, id } = data[dataIndex];

          if (menuItemId) {
            let menuItem = menuItems.find((x) => x.id === menuItemId);
            let path = "";

            if (paths && paths.hasOwnProperty(id)) {
              path = paths[id];
            }

            return menuItemId && path ? (
              <Tooltip
                key={`Tooltip-MenuItem-Name-${dataIndex}-${menuItemId}`}
                title={menuItem?.name ?? ""}
              >
                <Chip
                  key={`chip-MenuItem-${dataIndex}-${menuItemId}`}
                  color="default"
                  label={path ?? menuItem?.name}
                  size="small"
                  variant="outlined"
                />
              </Tooltip>
            ) : null;
          }
        },
        setCellHeaderProps: () => ({
          style: {
            whiteSpace: "nowrap",
            position: "sticky",
            left: 0,
            background: "white",
            zIndex: 101,
          },
        }),
        customFilterListOptions: {
          render: (v) => `Menu Item: ${getMenuItemName(v)}`,
        },
        filterOptions: {
          renderValue: (v) => getMenuItemName(v),
        },
      },
    },
    {
      label: "Filters",
      name: "layoutsFilters",
      options: {
        filter: false,
        setCellHeaderProps: () => ({
          style: {
            whiteSpace: "nowrap",
            position: "sticky",
            left: 0,
            background: "white",
            zIndex: 101,
          },
        }),
        customBodyRenderLite: (dataIndex) => {
          let filters = data[dataIndex]?.layoutFilters;
          const unique = Array.from(new Set(filters.map((s) => s.filterName))).map((filterName) => {
            return {
              name: filterName,
              countFilterValues: filters.find((f) => f.filterName === filterName).filterValues,
              type: filters.find((f) => f.filterName === filterName).type,
            };
          });

          return (
            <>
              {unique &&
                unique.map((l, i) => {
                  let name = l?.name;

                  if (name) {
                    return (
                      <Chip
                        key={`chip-${l}-${i}`}
                        avatar={
                          <Avatar
                            style={{
                              color: green[700],
                              backgroundColor: green[300],
                            }}
                          >
                            {name.slice(0, 1)}
                          </Avatar>
                        }
                        color="primary"
                        label={name}
                        size="small"
                        style={{
                          color: green[700],
                          borderColor: green[300],
                          margin: 3,
                        }}
                        variant="outlined"
                      />
                    );
                  }

                  return null;
                })}
            </>
          );
        },
      },
    },
    {
      name: "",
      label: "",
      options: {
        filter: false,
        sort: false,
        empty: true,
        viewColumns: false,
        setCellHeaderProps: () => ({
          style: {
            whiteSpace: "nowrap",
            position: "sticky",
            left: 0,
            background: "white",
            zIndex: 101,
          },
        }),
        customBodyRenderLite: (dataIndex) => {
          let rowData = data[dataIndex];

          return (
            <div style={{ maxWidth: 250 }}>
              <Tooltip title={"Edit"}>
                <IconButton aria-label="edit" size="small" onClick={handleClickOpen(rowData, true)}>
                  <EditIcon />
                </IconButton>
              </Tooltip>
              {/* <Tooltip title={`Preview`} >
                <IconButton
                  aria-label="preview"
                  onClick={handleClickPreview(rowData)}
                  size="small"
                >
                  <Visibility />
                </IconButton>
              </Tooltip> */}
              <Tooltip title={rowData.deleted ? "Is already removed" : "Delete"}>
                <IconButton
                  aria-label="delete"
                  disabled={rowData.deleted}
                  size="small"
                  onClick={handleConfirm(rowData)}
                >
                  <Delete />
                </IconButton>
              </Tooltip>
            </div>
          );
        },
        setCellProps: () => ({ style: { width: 130 } }),
      },
    },
  ];

  const options = {
    filterType: "dropdown",
    expandableRowsHeader: false,
    selectableRows: "none",
    // selectableRows: false,
    download: false,
    print: false,
    viewColumns: true,
    customToolbar: () => {
      return (
        <CustomToolbar handleClick={handleClickOpen(layout)} handleReload={handleClickReload} />
      );
    },
    //Dense
    setTableProps: () => {
      return {
        padding: "default",
        size: "small",
      };
    },
    textLabels: {
      body: {
        noMatch: loading ? "loading..." : "Sorry, no matching records found",
      },
    },
    setRowProps: (row, dataIndex, _rowIndex) => {
      let deleted = data[dataIndex]?.deleted;

      return (
        deleted && {
          style: { backgroundColor: "Gainsboro" },
          title: "Deleted",
        }
      );
    },
    responsive: "standard",
    fixedHeader: true,
    fixedSelectColumn: false,
    tableBodyHeight: "420px",
    jumpToPage: true,
  };

  return (
    <>
      <NavBar />
      <Paper className={classes.administrationPaper}>
        {loading && <BorderLinearProgress />}
        {/* <PreviewLayout  handleClose={handleClosePreview} open={openPreview} layout={layout} /> */}

        <ConfirmDelete
          handleClose={handleCloseConfirm}
          handleDelete={handleDelete}
          message={`Are you sure to Delete the Layout with Name: ${name}?`}
          open={openConfirm}
        />
        {open && layout && (
          <LayoutForm
            handleClose={handleClose}
            initialValues={layout}
            isEdit={isEdit}
            open={open}
          />
        )}
        <MUIDataTable columns={columns} data={data} options={options} title={"Layout"} />
      </Paper>
    </>
  );
};

export default Layout;
